import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import {
	markdownPreset,
	type MarkdownPresetBuilder,
	type MarkdownPresetProps,
} from '@atlassian/confluence-presets/markdown';

import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import {
	readStoredMarkdownViewMode,
	writeStoredMarkdownViewMode,
} from '@confluence/content-markdown-mode/entry-points/markdownViewMode.ts';
import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links/entry-points/headingLinksPlugin.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { getCodeBlockFormatProvider } from '../utils/codeBlockFormatProvider.ts';

import type { CreateFullPageEditorPresetProps } from './createFullPageEditorPresetNext.ts';
import { emptyStatePlugin } from './plugins/emptyStatePlugin.ts';
import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin.ts';
import { ssrPlugin } from './plugins/ssrPlugin.ts';

/**
 * Inputs for `createMarkdownEditorPreset`. Mirrors `CreateFullPageEditorPresetProps`
 * so `useFullPageEditorPreset` can pass the same `presetOptions` to either
 * function — the markdown variant only consumes the subset of fields that are
 * meaningful in markdown mode.
 */
export type CreateMarkdownEditorPresetProps = CreateFullPageEditorPresetProps;

type MarkdownProvidersWithFormatCodeProvider = MarkdownPresetProps['providers'] & {
	formatCodeProvider?: ReturnType<typeof getCodeBlockFormatProvider>;
};

const allowFormatCodeProvider = (
	providers: MarkdownProvidersWithFormatCodeProvider,
): MarkdownPresetProps['providers'] => providers as MarkdownPresetProps['providers'];

/**
 * Confluence's markdown-mode editor preset. Wraps the platform `markdownPreset`
 * with the Confluence-specific plugins that are still meaningful in markdown
 * mode (SSR, page-title utilities, empty-state detection, heading anchor
 * links) and supplies the `viewModePersistence` adapter that wires the
 * source/wysiwyg/preview toggle into `localStorage`.
 *
 * Plugin options, providers and `enabledOptionalPlugins` are derived from the
 * same flat `CreateFullPageEditorPresetProps` shape used by
 * `createFullPageEditorPreset`, so the two presets share a single source of
 * truth at the call site.
 */
export function createMarkdownEditorPreset(
	options: CreateMarkdownEditorPresetProps,
): MarkdownPresetBuilder {
	const { intl, appearance, prevAppearance } = options;
	const isEditorAIPluginEnabled = Boolean(options.editorPluginAIOptions);

	return markdownPreset({
		// @ts-expect-error — react-intl v5/v7 type mismatch, will be removed when platform upgrades to react-intl v7
		intl,
		markdownMode: {
			contentId: options.contentId ?? '',
			isMarkdownMode: true,
			// Required for file media to round-trip through GFM as `![alt](url)`
			// instead of being dropped on serialise. Mirrors `createFullPageEditorPresetNext.ts`.
			serializeFileMedia: options.serializeFileMediaForMarkdown,
			viewModePersistence: {
				getInitialView: readStoredMarkdownViewMode,
				onViewChange: writeStoredMarkdownViewMode,
			},
		},
		pluginOptions: {
			annotation: {
				annotationManager: options.annotationProviders?.annotationManager,
				createCommentExperience: fg('confluence_fe_create_inline_comment_exp_coverage_2')
					? createInlineCommentCompoundExperience
					: undefined,
				selectCommentExperience: selectInlineCommentCompoundExperience,
				viewInlineCommentTraceUFOPress: () => traceUFOPress('view-inline-comment-action'),
			},
			...(options.editorPluginAIOptions ? { ai: options.editorPluginAIOptions } : {}),
			aiDefinitions: options.editorPluginAIDefinitionsConfig,
			aiSuggestions: {
				contentAri: `ari:cloud:confluence:${options.cloudId ?? ''}:page/${options.contentId ?? ''}`,
			},
			avatarGroup: {
				collabEdit: options.collabEdit,
			},
			base: {
				__livePage: options.__livePage,
			},
			blockMenu: {
				getLinkPath: options.getHeadingLinkPath,
				blockLinkHashPrefix: options.blockLinkHashPrefix,
			},
			// Inline smart links in markdown mode. The markdown preset adds
			// cardPlugin with `onlyInlineCards: true`, so blockCard/embedCard
			// stay out of the schema even if the underlying CardPluginOptions
			// would otherwise enable them (allowBlockCards defaults to true).
			// Shape mirrors createFullPageEditorPresetNext so the two presets
			// share a single source of truth at the call site.
			card: {
				smartCardContext: isSSRStreaming() ? options.smartCardContext : undefined,
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				onClickCallback: options.linking?.smartLinks?.onClickCallback,
				__livePage: options.__livePage,
				CompetitorPrompt,
			},
			codeBidiWarning: {
				editorAppearance: appearance,
			},
			collabEdit: {
				collabEdit: options.collabEdit,
				__livePage: options.__livePage,
			},
			contentFormat: expValEquals('confluence_compact_text_format', 'isEnabled', true)
				? { initialContentMode: options.contentMode ?? 'standard' }
				: undefined,
			contextPanel: options.contextPanelOptions
				? { objectSideBar: options.contextPanelOptions.objectSideBar }
				: undefined,
			editorDisabled: {
				disabled: options.disabled,
			},
			editorViewMode: {
				viewMode: options.viewMode,
			},
			engagementPlatform: {
				coordinationClient: options.editorEngagementPlatformPluginConfig.coordinationClient,
			},
			helpDialog: {
				imageUploadProviderExists: false,
				aiEnabled: options.isAIEnabledForContent ?? false,
			},
			hyperlink: {
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				onClickCallback: options.linking?.smartLinks?.onClickCallback,
			},
			insertBlock: {
				editorAppearance: appearance,
				toolbarButtons: {
					insert: { enabled: true, showAt: 'lg' },
					media: { enabled: true, showAt: 'lg' },
					table: { enabled: true, showAt: 'md' },
					taskList: { enabled: true, showAt: 'lg' },
				},
			},
			limitedMode: options.limitedModePluginOptions || {},
			media: {
				createCommentExperience: fg('confluence_fe_create_inline_comment_exp_coverage_2')
					? createInlineCommentCompoundExperience
					: undefined,
				editorAppearance: appearance,
				mediaViewerExtensions: options.media?.mediaViewerExtensions,
				fallbackMediaNameFetcher: options.media?.fallbackMediaNameFetcher,
				// Captions have no clean GFM representation and the `caption` schema
				// node isn't registered in the markdown preset, so the caption button
				// would throw `Cannot read properties of undefined (reading 'create')`
				// when clicked. Hide the affordance entirely in markdown mode.
				allowCaptions: false,
				// GFM image syntax has no way to express dimensions, so resizing
				// would be silently lost on save. Hide the resize handles entirely.
				allowResizing: false,
			},
			metrics:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				expValEquals('platform_editor_controls', 'cohort', 'variant1')
					? { userPreferencesProvider: options.userPreferencesProvider }
					: undefined,
			placeholder: {
				viewMode: options.viewMode,
				isAIEnabled: options.isAIEnabledForContent ?? false,
				isMarkdownMode: true,
				isLivePage: options.__livePage ?? false,
				isPlaceholderHidden: false,
				enableLoadingSpinner: options.enablePlaceholderLoadingSpinner,
				isRovoLLMEnabled: options.isRovoLLMEnabled,
			},
			primaryToolbar: {
				contextualFormattingEnabled: options.contextualFormattingEnabled,
			},
			quickInsert: {
				emptyStateHandler: options.elementBrowser?.emptyStateHandler,
				quickInsert: options.quickInsert,
			},
			referentiality: {
				referentialityContext: options.referentialityContext,
			},
			selection: {
				__livePage: options.__livePage,
			},
			selectionExtension: options.selectionExtensionPluginOptions,
			selectionMarker: {
				__livePage: options.__livePage ?? false,
			},
			selectionToolbar: {
				contextualFormattingEnabled: options.contextualFormattingEnabled,
			},
			showDiff: options.showDiffPluginParams,
			table: {
				editorAppearance: appearance,
				prevEditorAppearance: prevAppearance,
			},
			tasksAndDecisions: {
				hasEditPermission: options.hasEditPermission,
				requestToEditContent: options.requestToEditContent,
				hasRequestedEditPermission: options.hasRequestedEditPermission,
			},
			toolbar: {
				contextualFormattingEnabled: options.contextualFormattingMode ?? 'controlled',
			},
			userPreferencesPlugin: {},
		},
		providers: allowFormatCodeProvider({
			autoformattingProvider: options.autoformattingProvider,
			cardProvider: options.linking?.smartLinks?.provider,
			collabEditProvider: options.collabEdit?.provider,
			contextIdentifierProvider: options.contextIdentifierProvider,
			formatCodeProvider: getCodeBlockFormatProvider(),
			inlineCommentAnnotationProvider: options.annotationProviders?.inlineComment,
			mediaProvider: options.media?.provider,
			syncMediaProvider: options.media?.syncProvider,
			taskDecisionProvider: options.taskDecisionProvider,
			userPreferencesProvider: options.userPreferencesProvider,
			userPreferencesProviderNext: options.userPreferencesProviderNext,
		}),
		enabledOptionalPlugins: {
			aiDefinitions: isEditorAIPluginEnabled && options.editorPluginAIDefinitionsEnabled,
			aiExperience: Boolean(
				isEditorAIPluginEnabled &&
				options.editorPluginAIOptions?.aiGlobalOptIn.status !== 'disabled',
			),
			aiImageGeneration:
				isEditorAIPluginEnabled &&
				expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
				isFeatureEnabled('confluence', 'confluence-ai'),
			aiSpeechToText:
				isEditorAIPluginEnabled && expValEquals('ai_speech_to_text_in_editor', 'isEnabled', true),
			aiStreamingOrchestrator: fg('aifc_create_enabled'),
			aiSuggestions:
				isEditorAIPluginEnabled &&
				expVal('platform_editor_ai_content_suggested_edits', 'isEnabled', false),
			connectivity:
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			contentFormat: expValEquals('confluence_compact_text_format', 'isEnabled', true),
			floatingToolbar: !options?.disableFloatingToolbar,
			interaction:
				Boolean(options?.__livePage) ||
				expValEquals('platform_editor_no_cursor_on_edit_page_init', 'isEnabled', true),
			localId: fg('platform_editor_adf_with_localid'),
			metrics: fg('platform_editor_ease_of_use_metrics'),
			referentiality: Boolean(options.referentialityContext.plugin),
			selectionExtension: expValEquals('platform_editor_controls', 'cohort', 'variant1'),
			showDiff: Boolean(options?.showDiffPluginParams) || fg('aifc_create_enabled'),
			toolbar:
				!options?.disableToolbar &&
				(fg('aifc_create_enabled') ||
					(isEditorAIPluginEnabled &&
						options.editorPluginAIOptions?.aiGlobalOptIn.status !== 'enabled' &&
						expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true))),
			userPreferences: fg('platform_editor_use_preferences_plugin'),
		},
	})
		.add(ssrPlugin)
		.add([EditorHeadingLinksPlugin, { getHeadingLinkPath: options.getHeadingLinkPath }])
		.add(pageTitleUtilsPlugin)
		.add(emptyStatePlugin) as unknown as MarkdownPresetBuilder;
}
